function normalizeFormId(id) {
  if (!id) return '';
  const trimmed = String(id).trim();
  return trimmed.startsWith('klaviyo-form-') ? trimmed.slice('klaviyo-form-'.length) : trimmed;
}

function extractFormIdFromElement(element) {
  for (const className of element.classList) {
    if (className.startsWith('klaviyo-form-') && className !== 'klaviyo-form-visible') {
      const formId = className.slice('klaviyo-form-'.length);
      if (formId) return formId;
    }
  }

  const testId = element.getAttribute('data-testid');
  if (testId?.startsWith('klaviyo-form-')) {
    return testId.slice('klaviyo-form-'.length);
  }

  return null;
}

function getSelectedVariant(productInfo, variantOverride) {
  if (variantOverride !== undefined) return variantOverride;

  const variantJson = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
  if (!variantJson) return null;

  try {
    return JSON.parse(variantJson);
  } catch (error) {
    return null;
  }
}

function isSubmitButtonSoldOut(productInfo) {
  const soldOutText = window.variantStrings?.soldOut;
  const unavailableText = window.variantStrings?.unavailable;
  const submitButton = productInfo.querySelector('[id^="ProductSubmitButton-"]');

  if (!submitButton || (!submitButton.disabled && !submitButton.hasAttribute('disabled'))) {
    return false;
  }

  const buttonText = (
    submitButton.querySelector('span:not(.loading__spinner)')?.textContent || submitButton.textContent || ''
  ).trim();

  if (unavailableText && buttonText.includes(unavailableText)) return false;
  if (!soldOutText) return true;

  return buttonText.includes(soldOutText);
}

function isSelectedVariantSoldOut(productInfo, variantOverride) {
  const variant = getSelectedVariant(productInfo, variantOverride);

  if (variant?.available === true) {
    return { isSoldOut: false, source: 'variant.available', variant };
  }

  if (variant?.available === false) {
    return { isSoldOut: true, source: 'variant.available', variant };
  }

  if (isSubmitButtonSoldOut(productInfo)) {
    return { isSoldOut: true, source: 'submitButton', variant };
  }

  return { isSoldOut: false, source: 'none', variant };
}

function getFormIdKey(formId) {
  return normalizeFormId(formId).toLowerCase();
}

function findEmbedContainer(embedsByFormId, targetFormId) {
  return embedsByFormId.get(getFormIdKey(targetFormId)) || null;
}

function setEmbedContainer(embedsByFormId, formId, container) {
  const key = getFormIdKey(formId);
  if (!key || !container) return;

  const existing = embedsByFormId.get(key);
  if (!existing) {
    embedsByFormId.set(key, container);
    return;
  }

  if (container.classList.contains('klaviyo-embed-wrapper') && !existing.classList.contains('klaviyo-embed-wrapper')) {
    embedsByFormId.set(key, container);
  }
}

function getEmbedFormIds(embedsByFormId) {
  return [...embedsByFormId.values()].map((container) => {
    if (container.dataset.klaviyoEmbedId) return container.dataset.klaviyoEmbedId;
    return extractFormIdFromElement(container) || extractFormIdFromElement(container.querySelector('[class*="klaviyo-form-"]'));
  }).filter(Boolean);
}

function getKlaviyoEmbeds(productInfo) {
  const embedsByFormId = new Map();

  productInfo.querySelectorAll('.klaviyo-embed-wrapper[data-klaviyo-embed-id]').forEach((wrapper) => {
    setEmbedContainer(embedsByFormId, wrapper.dataset.klaviyoEmbedId, wrapper);
  });

  productInfo.querySelectorAll('[class*="klaviyo-form-"]').forEach((element) => {
    const formId = extractFormIdFromElement(element);
    if (!formId || findEmbedContainer(embedsByFormId, formId)) return;

    const container = element.closest('.shopify-app-block[id*="klaviyo"]') || element;
    setEmbedContainer(embedsByFormId, formId, container);
  });

  return embedsByFormId;
}

function setContainerVisible(container, visible) {
  container.classList.toggle('klaviyo-form-visible', visible);
}

function setSoldOutButtonHidden(productInfo, hidden) {
  productInfo.querySelectorAll('.product-form__submit, [id^="ProductSubmitButton-"]').forEach((button) => {
    button.classList.toggle('hidden', hidden);
    button.toggleAttribute('hidden', hidden);
  });

  productInfo.querySelectorAll('.product-form__buttons').forEach((container) => {
    container.classList.toggle('hidden', hidden);
    container.toggleAttribute('hidden', hidden);
  });
}

function setBackInStockActive(productInfo, active) {
  productInfo.classList.toggle('klaviyo-back-in-stock-active', active);

  const infoContainer = productInfo.querySelector('.product__info-container');
  infoContainer?.classList.toggle('klaviyo-back-in-stock-active', active);
}

function logKlaviyoVisibilityState(productInfo, state) {
  console.log('[Klaviyo PDP]', {
    productId: productInfo.dataset.productId,
    metafieldFormId: state.targetFormId || '(empty)',
    requiredTag: productInfo.dataset.klaviyoTag || 'klaviyo-notify-me',
    hasTag: state.hasTag,
    isSoldOut: state.isSoldOut,
    selectedVariantId: state.variant?.id ?? null,
    selectedVariantAvailable: state.variant?.available ?? null,
    soldOutDetectionSource: state.soldOutDetectionSource,
    embedFormIds: state.embedFormIds,
    metafieldMatchesEmbed: state.metafieldMatchesEmbed,
    shouldShowForm: state.formIsVisible,
    hideSoldOutButton: state.formIsVisible,
  });
}

function updateKlaviyoFormVisibility(productInfo, variantOverride = undefined) {
  const targetFormId = normalizeFormId(productInfo.dataset.klaviyoFormId);
  const hasTag = productInfo.dataset.klaviyoTagEnabled === 'true';
  const { isSoldOut, source: soldOutDetectionSource, variant } = isSelectedVariantSoldOut(
    productInfo,
    variantOverride
  );
  const shouldShow = Boolean(targetFormId && hasTag && isSoldOut);

  const embedsByFormId = getKlaviyoEmbeds(productInfo);
  const embedFormIds = getEmbedFormIds(embedsByFormId);
  const metafieldMatchesEmbed = Boolean(targetFormId && findEmbedContainer(embedsByFormId, targetFormId));
  const formIsVisible = shouldShow && metafieldMatchesEmbed;

  embedsByFormId.forEach((container) => {
    setContainerVisible(container, false);
  });

  if (formIsVisible) {
    const container = findEmbedContainer(embedsByFormId, targetFormId);
    if (container) setContainerVisible(container, true);
  } else if (shouldShow && embedFormIds.length === 0) {
    console.warn(
      '[Klaviyo PDP] Notify-me conditions met but no Klaviyo embed found. Add a Klaviyo Form block in the Product information section and set its form ID to match the product metafield.',
      { metafieldFormId: targetFormId }
    );
  }

  setBackInStockActive(productInfo, formIsVisible);
  setSoldOutButtonHidden(productInfo, formIsVisible);

  logKlaviyoVisibilityState(productInfo, {
    targetFormId,
    hasTag,
    isSoldOut,
    variant,
    soldOutDetectionSource,
    embedFormIds,
    metafieldMatchesEmbed,
    formIsVisible,
  });
}

function mutationRequiresUpdate(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return false;

    return [...mutation.addedNodes].some((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;

      if (
        node.matches?.(
          '.klaviyo-embed-wrapper, [class*="klaviyo-form-"], [data-testid^="klaviyo-form-"], .shopify-app-block, .product-form__submit, .product-form__buttons, product-form, variant-selects'
        )
      ) {
        return true;
      }

      return Boolean(
        node.querySelector?.(
          '.klaviyo-embed-wrapper, [class*="klaviyo-form-"], [data-testid^="klaviyo-form-"], .product-form__submit, .product-form__buttons, variant-selects [data-selected-variant]'
        )
      );
    });
  });
}

class KlaviyoFormVisibilityController {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.observer = null;
    this.variantChangeUnsubscriber = null;
    this.debounceTimer = null;
    this.isUpdating = false;
  }

  get sectionId() {
    return this.productInfo.dataset.originalSection || this.productInfo.dataset.section;
  }

  update(variantOverride = undefined) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    try {
      updateKlaviyoFormVisibility(this.productInfo, variantOverride);
    } finally {
      this.isUpdating = false;
    }
  }

  scheduleUpdate() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.update(), 150);
  }

  init() {
    this.update();

    this.fallbackTimers = [500, 2000].map((delay) => setTimeout(() => this.update(), delay));

    this.onProductInfoLoaded = () => this.scheduleUpdate();
    this.onVariantChange = (event) => {
      if (event.data.sectionId !== this.sectionId) return;

      const variant = event.data.variant ?? null;
      this.update(variant);
      setTimeout(() => this.update(variant), 300);
    };

    this.productInfo.addEventListener('product-info:loaded', this.onProductInfoLoaded);
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);

    const container = this.productInfo.querySelector('.product__info-container') || this.productInfo;
    this.observer = new MutationObserver((mutations) => {
      if (mutationRequiresUpdate(mutations)) {
        this.scheduleUpdate();
      }
    });
    this.observer.observe(container, { childList: true, subtree: true });
  }

  destroy() {
    clearTimeout(this.debounceTimer);
    this.fallbackTimers?.forEach(clearTimeout);
    this.observer?.disconnect();
    this.variantChangeUnsubscriber?.();
    this.productInfo.removeEventListener('product-info:loaded', this.onProductInfoLoaded);
  }
}

function initKlaviyoFormVisibility() {
  document.querySelectorAll('product-info').forEach((productInfo) => {
    if (productInfo.dataset.klaviyoVisibilityInit === 'true') return;
    productInfo.dataset.klaviyoVisibilityInit = 'true';
    new KlaviyoFormVisibilityController(productInfo).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initKlaviyoFormVisibility);
} else {
  initKlaviyoFormVisibility();
}
